import React from 'react';
import {
    GAUGE_CX as CX,
    GAUGE_CY as CY,
    GAUGE_NEEDLE_LENGTH as NEEDLE_LENGTH,
    type GaugeZone,
    clampScore,
    gaugeBandPath as bandPath,
    gaugeNeedleRotation,
} from './gaugeGeometry';

interface MarketMoodGaugeProps {
    /** Score 0-100 de `market_summary_cache.market_mood`, ou null si le cache est absent. */
    score: number | null;
    theme: Record<string, string>;
}

// Seuils IDENTIQUES à api/app/market_cache_service.py::_calculate_market_summary
// (mood > 60 -> BULLISH, mood < 40 -> BEARISH, sinon NEUTRAL). Ce verdict est aussi celui
// transmis à l'assistant IA (rag_indexer.py) : la jauge ne doit jamais dire autre chose.
export const MOOD_BEARISH_BELOW = 40;
export const MOOD_BULLISH_ABOVE = 60;

type Verdict = { label: string; color: string };

const BEARISH: Verdict = { label: 'BAISSIER', color: '#ef5350' };
const NEUTRAL: Verdict = { label: 'NEUTRE', color: '#fdd835' };
const BULLISH: Verdict = { label: 'HAUSSIER', color: '#00b638' };

export const moodVerdict = (score: number): Verdict =>
    score > MOOD_BULLISH_ABOVE ? BULLISH : score < MOOD_BEARISH_BELOW ? BEARISH : NEUTRAL;

const ZONES: GaugeZone[] = [
    { from: 0, to: MOOD_BEARISH_BELOW, color: BEARISH.color },
    { from: MOOD_BEARISH_BELOW, to: MOOD_BULLISH_ABOVE, color: NEUTRAL.color },
    { from: MOOD_BULLISH_ABOVE, to: 100, color: BULLISH.color },
];

export const MarketMoodGauge: React.FC<MarketMoodGaugeProps> = ({ score, theme }) => {
    const available = score != null && Number.isFinite(score);
    const clamped = available ? clampScore(score as number) : 50;
    const verdict = available ? moodVerdict(clamped) : null;
    const needleRotation = gaugeNeedleRotation(clamped);

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <svg
                viewBox='0 0 200 108'
                style={{ width: '100%', maxWidth: 240 }}
                role='img'
                aria-label={verdict ? `Sentiment de marché : ${verdict.label.toLowerCase()}` : 'Sentiment de marché indisponible'}
            >
                {ZONES.map(zone => (
                    <path
                        key={zone.from}
                        d={bandPath(zone.from, zone.to)}
                        fill={zone.color}
                        opacity={verdict ? 1 : 0.25}
                    />
                ))}
                {verdict && (
                    <g
                        data-testid='mood-needle'
                        style={{
                            transform: `rotate(${needleRotation}deg)`,
                            transformOrigin: `${CX}px ${CY}px`,
                            transition: 'transform 1s ease-out',
                        }}
                    >
                        <line x1={CX} y1={CY} x2={CX} y2={CY - NEEDLE_LENGTH} stroke={theme.text} strokeWidth={2.5} strokeLinecap='round' />
                    </g>
                )}
                <circle cx={CX} cy={CY} r={5} fill={theme.text} />
            </svg>

            <div style={{ marginTop: 8, textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: theme.sub, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Sentiment de Marché
                </div>
                <div style={{ marginTop: 4, fontSize: '18px', fontWeight: 800, color: verdict ? verdict.color : theme.sub }}>
                    {verdict ? verdict.label : 'Données indisponibles'}
                </div>
            </div>
        </div>
    );
};
